import { type ImageRatioType, OPTIMAL_DIMENSIONS } from '@/shared/utils';
import imageCompression from 'browser-image-compression';
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from 'firebase/storage';
import { storage } from '../firebase';

export const StorageService = {
  /**
   * Procesa una imagen antes de subirla (Compresión, Cambio a WebP, Ajuste de Dimensiones)
   */
  processImageForUpload: async (
    file: File,
    ratioType: ImageRatioType,
  ): Promise<File> => {
    // The ultimate config for max quality & min size
    const options = {
      maxWidthOrHeight: OPTIMAL_DIMENSIONS[ratioType],
      initialQuality: 0.85, // 85% is the sweet spot. Visually lossless, but drops size by ~70%
      useWebWorker: true,
      fileType: 'image/webp', // Forces WebP. Massive velocity upgrade over JPEG/PNG.
      alwaysKeepResolution: true, // Prevents it from shrinking images that are already small
    };

    try {
      // 1. Compress and resize
      const compressedBlob = await imageCompression(file, options);

      // 2. Convert back to a File object for Firebase Storage
      const optimizedFile = new File(
        [compressedBlob],
        file.name.replace(/\.[^/.]+$/, '.webp'), // Change extension to .webp
        { type: 'image/webp' },
      );

      return optimizedFile;
    } catch (error) {
      console.error(
        '[Storage] ❌ Error al procesar estética de imagen:',
        error,
      );
      throw error;
    }
  },

  /**
   * Sube un archivo a una ruta específica en su tamaño y formato original, retornando la URL pública
   */
  uploadFile: async (file: File, path: string): Promise<string> => {
    try {
      const storageRef = ref(storage, path);
      // Metadatos para que el navegador sepa que es una imagen y la cachee bien
      const metadata = { contentType: file.type };

      const snapshot = await uploadBytes(storageRef, file, metadata);
      return await getDownloadURL(snapshot.ref);
    } catch (error) {
      console.error('[Storage] ❌ Error:', error);
      throw error;
    }
  },

  /**
   * Elimina un archivo mediante su URL completa
   */
  deleteFile: async (fileUrl: string): Promise<void> => {
    try {
      const fileRef = ref(storage, fileUrl);
      await deleteObject(fileRef);
    } catch (error) {
      console.error('[Storage] ❌ Error al eliminar archivo:', error);
      // Opcional: throw error si necesitas manejarlo en la UI
    }
  },

  /**
   * Helper para generar rutas consistentes
   */
  generatePath: (
    ownerId: string,
    type: 'logo' | 'banners' | 'catalog' | 'billing',
    fileName: string,
  ) => {
    const cleanName = fileName.replace(/\s+/g, '-').toLowerCase();
    if (type === 'logo') return `owners/${ownerId}/branding/logo/main_logo`;
    if (type === 'banners')
      return `owners/${ownerId}/branding/banners/${Date.now()}-${cleanName}`;
    if (type === 'catalog')
      return `owners/${ownerId}/branding/catalog/${Date.now()}-${cleanName}`;
    if (type === 'billing')
      return `owners/${ownerId}/admin/billing/${Date.now()}-${cleanName}`;
    return `owners/${ownerId}/branding/misc/${Date.now()}-${cleanName}`;
  },
};
